import { CheckCircle, Replay, ReportProblem } from "@mui/icons-material";
import { Alert, Button, Paper, Stack, Typography } from "@mui/material";
import { useTranslate } from "react-admin";
import { Link } from "react-router-dom";

import { LearningQuizResult } from "../../../hooks/useLearningCommands";
import { borderRadius, color, shadows, space } from "../../../theme/designTokens";

type QuizResultProps = {
  result: LearningQuizResult;
  onRetry: () => void;
};

export const QuizResult = ({ result, onRetry }: QuizResultProps) => {
  const translate = useTranslate();
  return (
    <Paper
      data-testid="learning-quiz-result"
      variant="outlined"
      sx={{
        p: { xs: space.xl, md: space.xxl1 },
        borderRadius: borderRadius.md,
        borderColor: color.panelBorder,
        boxShadow: shadows.card,
      }}
    >
      <Stack spacing={space.xl} alignItems="flex-start">
        {result.serverPassed ? (
          <CheckCircle color="success" fontSize="large" />
        ) : (
          <ReportProblem color="warning" fontSize="large" />
        )}
        <Typography component="h1" variant="h4">
          {translate(result.serverPassed ? "learning.quiz.passed" : "learning.quiz.failed")}
        </Typography>
        <Typography variant="h5">{translate("learning.quiz.score", { score: result.serverScorePercent })}</Typography>
        <Typography color="text.secondary">
          {translate("learning.quiz.correctCount", { correct: result.serverCorrect, total: result.serverTotal })}
        </Typography>
        {!result.serverPassed && <Alert severity="info">{translate("learning.quiz.retryHelp")}</Alert>}
        <Stack direction={{ xs: "column", sm: "row" }} gap={space.md}>
          <Button component={Link} to={`/learning/courses/${result.courseId}`}>
            {translate("learning.player.backToCourse")}
          </Button>
          <Button data-testid="learning-quiz-retry" variant="contained" startIcon={<Replay />} onClick={onRetry}>
            {translate("learning.quiz.retry")}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};
